import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { apiService } from '../../services/apiService';

export const fetchParts = createAsyncThunk(
    'parts/fetchParts',
    async (params, { rejectWithValue }) => {
        try {
            const response = await apiService.getPartsPaginated(params);
            return response;
        } catch (error) {
            return rejectWithValue(error || 'Failed to fetch Parts');
        }
    }
);

export const fetchPartsOverview = createAsyncThunk(
    'parts/fetchPartsOverview',
    async (_, { rejectWithValue }) => {
        try {
            const response = await apiService.getPartsOverview();
            return response;
        } catch (error) {
            return rejectWithValue(error || 'Failed to fetch Parts overview');
        }
    }
);

export const deleteParts = createAsyncThunk(
    'parts/deleteParts',
    async (id, { rejectWithValue }) => {
        try {
            const response = await apiService.deleteParts(id);
            return response;
        } catch (error) {
            return rejectWithValue(error || 'Failed to delete Parts');
        }
    }
);

export const partCreate = createAsyncThunk(
    'parts/partCreate',
    async (data, { rejectWithValue }) => {
        try {
            const response = await apiService.createPart(data);
            return response;
        } catch (error) {
            return rejectWithValue(error || 'Failed to create Part');
        }
    }
);

export const getPartDetail = createAsyncThunk(
    'parts/getPartDetail',
    async (id, { rejectWithValue }) => {
        try {
            const response = await apiService.getPartDetail(id);
            return response;
        } catch (error) {
            return rejectWithValue(error || 'Failed to fetch data');
        }
    }
);

export const getPartPaginatedForPO = createAsyncThunk(
    'parts/getPartPaginatedForPO',
    async (params, { rejectWithValue }) => {
        try {
            const response = await apiService.getPartPaginatedForPO(params);
            return response;
        } catch (error) {
            return rejectWithValue(error || 'Failed to fetch part procurement information');
        }
    }
);

export const getPartUtilization = createAsyncThunk(
    'parts/getPartUtilization',
    async (params, { rejectWithValue }) => {
        try {
            const response = await apiService.getPartUtilization(params);
            return response;
        } catch (error) {
            return rejectWithValue(error || 'Failed to fetch part utilization information');
        }
    }
);

export const getPartHistory = createAsyncThunk(
    "parts/getPartHistory",
    async (params, { rejectWithValue }) => {
        try {
            const response = await apiService.getPartHistory(params);
            return response;
        } catch (error) {
            return rejectWithValue(error || "Failed to get part history");
        }
    }
);

export const commentOnParts = createAsyncThunk(
    "parts/commentOnParts",
    async (data, { rejectWithValue }) => {
        try {
            const response = await apiService.commentOnParts(data);
            return response;
        } catch (error) {
            return rejectWithValue(error || "Failed to comment on parts");
        }
    }
);

export const partUpdate = createAsyncThunk(
    'parts/partUpdate',
    async ({ data }, { rejectWithValue }) => {
        try {
            const response = await apiService.updatePart({ data });
            return response;
        } catch (error) {
            return rejectWithValue(error || 'Failed to update Part');
        }
    }
);

const initialState = {
    getPartsPaginated: [],
    getPartsLoading: false,
    getPartsError: null,
    fetchPartsOverviewData: null,
    fetchPartsOverviewLoading: false,
    fetchPartsOverviewError: null,

    deletePartsLoading: false,
    deletePartsError: null,

    partCreateLoading: false,
    partCreateError: null,

    getPartDetailLoading: false,
    getPartDetailData: [],
    getPartDetailError: null,

    getPartPaginatedForPOloading: false,
    getPartPaginatedForPOData: null,
    getPartPaginatedForPOError: null,

    getPartUtilizationLoading: false,
    getPartUtilizationData: null,
    getPartUtilizationError: null,

    getPartHistoryLoading: false,
    getPartHistoryData: [],
    getPartHistoryError: null,

    commentOnPartsLoading: false,
    commentOnPartsData: [],
    commentOnPartsError: null,

    partUpdateLoading: false,
    partUpdateError: null,
}

const partSlice = createSlice({
    name: 'parts',
    initialState,
    reducers: {},
    extraReducers: (builder) => {
        builder

            .addCase(fetchParts.pending, (state) => {
                state.getPartsLoading = true;
                state.getPartsError = null;
            })
            .addCase(fetchParts.fulfilled, (state, action) => {
                state.getPartsLoading = false;
                state.getPartsPaginated = action.payload?.data || [];
                state.getPartsError = null;
            })
            .addCase(fetchParts.rejected, (state, action) => {
                state.getPartsLoading = false;
                state.getPartsError = action.payload;
                state.getPartsPaginated = [];
            })
            .addCase(fetchPartsOverview.pending, (state) => {
                state.fetchPartsOverviewLoading = true;
                state.fetchPartsOverviewError = null;
            })
            .addCase(fetchPartsOverview.fulfilled, (state, action) => {
                state.fetchPartsOverviewLoading = false;
                state.fetchPartsOverviewData = action.payload?.data || null;
                state.fetchPartsOverviewError = null;
            })
            .addCase(fetchPartsOverview.rejected, (state, action) => {
                state.fetchPartsOverviewLoading = false;
                state.fetchPartsOverviewError = action.payload;
            })

            .addCase(deleteParts.pending, (state) => {
                state.deletePartsLoading = true;
                state.deletePartsError = null;
            })
            .addCase(deleteParts.fulfilled, (state, action) => {
                state.deletePartsLoading = false;
                state.deletePartsError = null;
            })
            .addCase(deleteParts.rejected, (state, action) => {
                state.deletePartsLoading = false;
                state.deletePartsError = action.payload;
            })

            .addCase(partCreate.pending, (state) => {
                state.partCreateLoading = true;
                state.partCreateError = null;
            })
            .addCase(partCreate.fulfilled, (state, action) => {
                state.partCreateLoading = false;
                state.partCreateError = null;
            })
            .addCase(partCreate.rejected, (state, action) => {
                state.partCreateLoading = false;
                state.partCreateError = action.payload;
            })

            .addCase(getPartDetail.pending, (state) => {
                state.getPartDetailLoading = true;
                state.getPartDetailError = null;
            })
            .addCase(getPartDetail.fulfilled, (state, action) => {
                state.getPartDetailLoading = false;
                state.getPartDetailData = action.payload?.data;
                state.getPartDetailError = null;
            })
            .addCase(getPartDetail.rejected, (state, action) => {
                state.getPartDetailLoading = false;
                state.getPartDetailError = action.payload;
            })
            .addCase(getPartPaginatedForPO.pending, (state) => {
                state.getPartPaginatedForPOloading = true;
                state.getPartPaginatedForPOError = null;
            })
            .addCase(getPartPaginatedForPO.fulfilled, (state, action) => {
                state.getPartPaginatedForPOloading = false;
                state.getPartPaginatedForPOData = action.payload?.data || null;
                state.getPartPaginatedForPOError = null;
            })
            .addCase(getPartPaginatedForPO.rejected, (state, action) => {
                state.getPartPaginatedForPOloading = false;
                state.getPartPaginatedForPOError = action.payload;
            })
            .addCase(getPartUtilization.pending, (state) => {
                state.getPartUtilizationLoading = true;
                state.getPartUtilizationError = null;
            })
            .addCase(getPartUtilization.fulfilled, (state, action) => {
                state.getPartUtilizationLoading = false;
                state.getPartUtilizationData = action.payload?.data || null;
                state.getPartUtilizationError = null;
            })
            .addCase(getPartUtilization.rejected, (state, action) => {
                state.getPartUtilizationLoading = false;
                state.getPartUtilizationError = action.payload;
            })

            .addCase(getPartHistory.pending, (state) => {
                state.getPartHistoryLoading = true;
                state.getPartHistoryError = null;
            })
            .addCase(getPartHistory.fulfilled, (state, action) => {
                state.getPartHistoryLoading = false;
                state.getPartHistoryData = action.payload?.data;
                state.getPartHistoryError = null;
            })
            .addCase(getPartHistory.rejected, (state, action) => {
                state.getPartHistoryLoading = false;
                state.getPartHistoryError = action.payload;
            })

            .addCase(commentOnParts.pending, (state) => {
                state.commentOnPartsLoading = true;
                state.commentOnPartsError = null;
            })
            .addCase(commentOnParts.fulfilled, (state, action) => {
                state.commentOnPartsLoading = false;
                state.commentOnPartsData = action.payload?.data;
                state.commentOnPartsError = null;
            })
            .addCase(commentOnParts.rejected, (state, action) => {
                state.commentOnPartsLoading = false;
                state.commentOnPartsError = action.payload;
            })

            .addCase(partUpdate.pending, (state) => {
                state.partUpdateLoading = true;
                state.partUpdateError = null;
            })
            .addCase(partUpdate.fulfilled, (state, action) => {
                state.partUpdateLoading = false;
                state.partUpdateError = null;
            })
            .addCase(partUpdate.rejected, (state, action) => {
                state.partUpdateLoading = false;
                state.partUpdateError = action.payload;
            })
    },
});

export default partSlice.reducer;
